/**
 * API Data Fix - Ensures API responses never contain null values that would crash components
 * This works by intercepting fetch and XMLHttpRequest responses to sanitize the data
 */
export default function applyApiFix() {
  console.log('Loading API Data Fix...');
  
  // Keep API response history for debugging
  const apiResponseLog = [];
  const MAX_LOG_SIZE = 20;
  
  // Helper function to log and store API responses
  function logApiResponse(source, url, rawData, sanitizedData, error) {
    const entry = {
      timestamp: new Date().toISOString(),
      source,
      url,
      hasData: !!rawData,
      dataSize: rawData ? JSON.stringify(rawData).length : 0,
      changed: rawData !== sanitizedData,
      error: error || null
    };
    
    // Add to internal log
    apiResponseLog.unshift(entry);
    
    // Keep log at reasonable size
    if (apiResponseLog.length > MAX_LOG_SIZE) {
      apiResponseLog.pop();
    }
    
    // Log to console
    console.log(`[ApiDataFix] ${source} response from ${url}`, entry);
    
    // Log to DEBUG if available
    if (window.DEBUG) {
      window.DEBUG.log('ApiDataFix', `${source} response from ${url}`, {
        ...entry,
        // Add samples of the data for debugging
        rawSample: rawData ? JSON.stringify(rawData).substring(0, 200) + '...' : null,
        sanitizedSample: sanitizedData ? JSON.stringify(sanitizedData).substring(0, 200) + '...' : null
      });
      
      // Store full responses in sessionStorage for detailed inspection
      try {
        const detailedLogs = JSON.parse(sessionStorage.getItem('api_detailed_logs') || '[]');
        
        // Store a more complete entry with data samples
        detailedLogs.unshift({
          ...entry,
          rawSample: rawData ? JSON.stringify(rawData).substring(0, 500) : null,
          sanitizedSample: sanitizedData ? JSON.stringify(sanitizedData).substring(0, 500) : null
        });
        
        // Keep only recent entries
        if (detailedLogs.length > 5) detailedLogs.pop();
        
        sessionStorage.setItem('api_detailed_logs', JSON.stringify(detailedLogs));
      } catch (e) {
        // Ignore storage errors
      }
    }
  }
  
  // Expose API log to global scope for debugging
  window.API_RESPONSE_LOG = {
    getLog: function() {
      return [...apiResponseLog];
    },
    clearLog: function() {
      apiResponseLog.length = 0;
      return 'Log cleared';
    },
    getDetailedLogs: function() {
      try {
        return JSON.parse(sessionStorage.getItem('api_detailed_logs') || '[]');
      } catch (e) {
        return [];
      }
    }
  };
  
  // Helper function to sanitize data
  function sanitizeData(data) {
    if (!data) return {};
    
    // If it's an array, sanitize each item
    if (Array.isArray(data)) {
      // Ensure it's never empty to prevent components from crashing
      if (data.length === 0) {
        return [{}]; // Return array with empty object to prevent crashes
      }
      return data.map(item => sanitizeData(item));
    }
    
    // If it's an object, sanitize each property
    if (typeof data === 'object' && data !== null) {
      const result = {};
      for (const key in data) {
        result[key] = sanitizeData(data[key]);
      }
      
      // FIRST: Check if this is a payment methods response that needs transformation
      if (data && data.data && data.data.quickbooksGetCompanyPaymentMethods) {
        console.log('🔄 [ApiDataFix sanitizeData] Found payment methods in sanitizeData, transforming...');
        transformPaymentMethodsData(data);
      }
      
      // Handle specific QuickBooks API responses
      // Support both old nested structure (data.data.results.body) and new simplified structure
      const hasOldStructure = data.statusCode && data.data && data.data.results && data.data.results.body;
      const hasNewStructure = data.statusCode && data.data && data.data.results && Array.isArray(data.data.results);
      
      if (hasOldStructure || hasNewStructure) {
        // Log that we're specifically handling a QuickBooks response
        if (window.DEBUG) {
          window.DEBUG.log('ApiDataFix', 'Processing QuickBooks response', {
            statusCode: data.statusCode,
            hasOldStructure: hasOldStructure,
            hasNewStructure: hasNewStructure,
            hasQueryResponse: hasOldStructure ? !!data.data.results.body.QueryResponse : false,
            hasInvoice: hasOldStructure ? !!data.data.results.body.Invoice : false,
            hasCustomer: hasOldStructure ? !!data.data.results.body.Customer : false
          });
        }
        
        // Transform new structure to old structure if needed
        if (hasNewStructure && !hasOldStructure) {
          // Convert simplified array format to nested structure
          data.data.results = {
            body: {
              QueryResponse: {
                PaymentMethod: data.data.results
              }
            }
          };
        }
        
        // Handle QueryResponse for terms, tax rates, and payment methods
        // Support both old structure (data.data.results.body.QueryResponse) and new structure
        let qr = null;
        if (data.data && data.data.results) {
          if (data.data.results.body && data.data.results.body.QueryResponse) {
            qr = data.data.results.body.QueryResponse;
          } else if (data.data.results.QueryResponse) {
            // Results might already be the body
            qr = data.data.results.QueryResponse;
          }
        }
        
        if (qr) {
          
          // Fix Term array
          if ('Term' in qr) {
            const originalTerm = qr.Term;
            if (!qr.Term) qr.Term = [{}];
            if (!Array.isArray(qr.Term)) qr.Term = [qr.Term];
            
            if (window.DEBUG && qr.Term !== originalTerm) {
              window.DEBUG.log('ApiDataFix', 'Fixed Term array', {
                before: originalTerm,
                after: qr.Term
              });
            }
          }
          
          // Fix TaxRate array
          if ('TaxRate' in qr) {
            const originalTaxRate = qr.TaxRate;
            if (!qr.TaxRate) qr.TaxRate = [{}];
            if (!Array.isArray(qr.TaxRate)) qr.TaxRate = [qr.TaxRate];
            
            if (window.DEBUG && qr.TaxRate !== originalTaxRate) {
              window.DEBUG.log('ApiDataFix', 'Fixed TaxRate array', {
                before: originalTaxRate,
                after: qr.TaxRate
              });
            }
          }
          
          // Fix TaxCode array
          if ('TaxCode' in qr) {
            const originalTaxCode = qr.TaxCode;
            if (!qr.TaxCode) qr.TaxCode = [{}];
            if (!Array.isArray(qr.TaxCode)) qr.TaxCode = [qr.TaxCode];
            
            if (window.DEBUG && qr.TaxCode !== originalTaxCode) {
              window.DEBUG.log('ApiDataFix', 'Fixed TaxCode array', {
                before: originalTaxCode,
                after: qr.TaxCode
              });
            }
          }
          
          // Fix PaymentMethod array (for payment methods dropdown)
          if ('PaymentMethod' in qr) {
            const originalPaymentMethod = qr.PaymentMethod;
            if (!qr.PaymentMethod) qr.PaymentMethod = [];
            if (!Array.isArray(qr.PaymentMethod)) qr.PaymentMethod = [qr.PaymentMethod];
            
            if (window.DEBUG && qr.PaymentMethod !== originalPaymentMethod) {
              window.DEBUG.log('ApiDataFix', 'Fixed PaymentMethod array', {
                before: originalPaymentMethod,
                after: qr.PaymentMethod
              });
            }
          }
        }
        
        // Handle Invoice data specifically
        if (data.data.results && data.data.results.body && data.data.results.body.Invoice) {
          const invoice = data.data.results.body.Invoice;
          
          // Ensure Line array exists and is properly formatted
          if (!invoice.Line) {
            invoice.Line = [];
          } else if (!Array.isArray(invoice.Line)) {
            invoice.Line = [invoice.Line];
          }
          
          // Ensure each line item has required fields
          invoice.Line = invoice.Line.map(line => ({
            Amount: line.Amount || 0,
            Description: line.Description || '',
            DetailType: line.DetailType || 'SalesItemLineDetail',
            SalesItemLineDetail: {
              ItemRef: {
                value: (line.SalesItemLineDetail && line.SalesItemLineDetail.ItemRef && line.SalesItemLineDetail.ItemRef.value) || '',
                name: (line.SalesItemLineDetail && line.SalesItemLineDetail.ItemRef && line.SalesItemLineDetail.ItemRef.name) || ''
              },
              UnitPrice: (line.SalesItemLineDetail && line.SalesItemLineDetail.UnitPrice) || 0,
              Qty: (line.SalesItemLineDetail && line.SalesItemLineDetail.Qty) || 1,
              TaxCodeRef: {
                value: (line.SalesItemLineDetail && line.SalesItemLineDetail.TaxCodeRef && line.SalesItemLineDetail.TaxCodeRef.value) || ''
              }
            }
          }));
          
          if (window.DEBUG) {
            window.DEBUG.log('ApiDataFix', 'Processed Invoice data', {
              hasLine: !!invoice.Line,
              lineCount: invoice.Line.length,
              sampleLine: invoice.Line[0]
            });
          }
        }
      }
      
      return result;
    }
    
    // If it's null or undefined, return a safe default
    if (data === null || data === undefined) {
      return '';
    }
    
    // For all other values, return as is
    return data;
  }
  
  // Helper to transform payment methods response structure
  function transformPaymentMethodsData(data) {
    if (data && data.data && data.data.quickbooksGetCompanyPaymentMethods) {
      const result = data.data.quickbooksGetCompanyPaymentMethods;
      console.log('🔄 [ApiDataFix] Found payment methods response:', {
        statusCode: result.statusCode,
        hasData: !!result.data,
        hasResults: !!result.data && !!result.data.results,
        resultsIsArray: result.data && result.data.results && Array.isArray(result.data.results),
        hasRaw: !!result.data && !!result.data.raw,
        hasRawQueryResponse: !!result.data && !!result.data.raw && !!result.data.raw.QueryResponse
      });
      
      // PREFERRED: If we have the raw QuickBooks response, use that directly
      if (result.data && result.data.raw && result.data.raw.QueryResponse && result.data.raw.QueryResponse.PaymentMethod) {
        console.log('🔄 [ApiDataFix] Using raw QuickBooks response structure');
        const paymentMethods = result.data.raw.QueryResponse.PaymentMethod;
        const paymentMethodsArray = Array.isArray(paymentMethods) ? paymentMethods : [paymentMethods];
        
        // Transform to match frontend expectations: data.results.body.QueryResponse.PaymentMethod
        result.data.results = {
          body: {
            QueryResponse: {
              PaymentMethod: paymentMethodsArray
            }
          }
        };
        
        console.log('✅ [ApiDataFix] Payment methods transformed from raw structure');
        console.log('✅ [ApiDataFix] Payment methods count:', paymentMethodsArray.length);
        return true;
      }
      // FALLBACK: If we have the simplified format (array), transform it to nested structure
      else if (result.data && result.data.results && Array.isArray(result.data.results)) {
        console.log('🔄 [ApiDataFix] Transforming payment methods from array to nested structure');
        console.log('🔄 [ApiDataFix] Array length:', result.data.results.length);
        
        const qbFormatPaymentMethods = result.data.results.map(pm => ({
          Id: pm.id,
          Name: pm.name,
          Type: pm.type,
          Active: pm.active,
          domain: 'QBO',
          sparse: false,
          SyncToken: '0'
        }));
        
        // Transform to match frontend expectations: data.results.body.QueryResponse.PaymentMethod
        result.data.results = {
          body: {
            QueryResponse: {
              PaymentMethod: qbFormatPaymentMethods
            }
          }
        };
        
        console.log('✅ [ApiDataFix] Payment methods transformed from array to nested structure');
        return true;
      } else {
        console.warn('⚠️ [ApiDataFix] Payment methods response structure unexpected:', {
          hasData: !!result.data,
          hasResults: !!result.data && !!result.data.results,
          resultsType: result.data && result.data.results ? typeof result.data.results : 'undefined',
          isArray: result.data && result.data.results && Array.isArray(result.data.results),
          hasRaw: !!result.data && !!result.data.raw
        });
      }
    }
    return false;
  }
  
  // Override fetch to sanitize API responses
  const originalFetch = window.fetch;
  window.fetch = function(url, options) {
    // Log the fetch request
    if (window.DEBUG) {
      window.DEBUG.log('ApiDataFix', 'Fetch request', {
        url,
        method: options ? options.method : 'GET'
      });
    }
    
    // Check if this is a payment methods query
    const isPaymentMethodsQuery = options && options.body && typeof options.body === 'string' && 
      options.body.includes('quickbooksGetCompanyPaymentMethods');
    
    return originalFetch.apply(this, arguments)
      .then(response => {
        const originalJson = response.json;
        response.json = function() {
          return originalJson.apply(this)
            .then(data => {
              console.log('🔄 [ApiDataFix] Response.json() called, data structure:', {
                hasData: !!data,
                hasDataData: !!(data && data.data),
                hasQuickbooksGetCompanyPaymentMethods: !!(data && data.data && data.data.quickbooksGetCompanyPaymentMethods),
                isPaymentMethodsQuery: isPaymentMethodsQuery,
                url: url
              });
              
              // ALWAYS check for payment methods response structure (more reliable than query string)
              if (data && data.data && data.data.quickbooksGetCompanyPaymentMethods) {
                console.log('🔄 [ApiDataFix] ✅ Detected payment methods response in data structure');
                const transformed = transformPaymentMethodsData(data);
                if (!transformed) {
                  console.warn('⚠️ [ApiDataFix] transformPaymentMethodsData returned false - transformation may have failed');
                }
              } else if (isPaymentMethodsQuery) {
                console.log('🔄 [ApiDataFix] Processing payment methods query (from query string)');
                const transformed = transformPaymentMethodsData(data);
                if (!transformed) {
                  console.warn('⚠️ [ApiDataFix] transformPaymentMethodsData returned false - transformation may have failed');
                }
              } else {
                console.log('ℹ️ [ApiDataFix] Not a payment methods query, skipping transformation');
              }
              
              // THEN: Check if this is likely a QuickBooks API response and sanitize
              const isQuickbooksData = data && 
                ((data.statusCode && data.data && data.data.results) || 
                 (data.data && data.data.QueryResponse));
              
              if (isQuickbooksData) {
                console.log('Intercepted and sanitizing QuickBooks API response');
                
                try {
                  const sanitizedData = sanitizeData(data);
                  logApiResponse('fetch', url, data, sanitizedData);
                  return sanitizedData;
                } catch (error) {
                  logApiResponse('fetch', url, data, data, error);
                  console.error('Error sanitizing API response:', error);
                  return data; // Return original data if sanitization fails
                }
              }
              
              return data;
            });
        };
        return response;
      })
      .catch(error => {
        if (window.DEBUG) {
          window.DEBUG.error('ApiDataFix', `Fetch request error for ${url}`, error);
        }
        throw error; // Re-throw to not interrupt normal error handling
      });
  };
  
  // Also patch XMLHttpRequest for legacy API calls
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;
  
  XMLHttpRequest.prototype.open = function() {
    this._url = arguments[1]; // Store the URL for later checking
    return originalXhrOpen.apply(this, arguments);
  };
  
  XMLHttpRequest.prototype.send = function() {
    const xhr = this;
    const originalOnReadyStateChange = xhr.onreadystatechange;
    
    // Log the request
    if (window.DEBUG) {
      window.DEBUG.log('ApiDataFix', 'XHR request', {
        url: xhr._url,
        method: xhr._method || 'GET'
      });
    }
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        // Check if this is a payment methods query
        const isPaymentMethodsQuery = xhr._url && xhr._url.includes('/graphql') && 
          xhr.responseText && xhr.responseText.includes('quickbooksGetCompanyPaymentMethods');
        
        // Check if this is likely a GraphQL call related to QuickBooks
        const isQuickbooksRelated = xhr.responseText && 
            (xhr._url.includes('/graphql') || xhr._url.includes('/api')) && 
            (xhr.responseText.includes('quickbooks') || 
             xhr.responseText.includes('TaxRate') || 
             xhr.responseText.includes('Term') ||
             isPaymentMethodsQuery);
             
        if (isQuickbooksRelated) {
          try {
            const data = JSON.parse(xhr.responseText);
            
            // ALWAYS check for payment methods response structure (more reliable than query string)
            if (data && data.data && data.data.quickbooksGetCompanyPaymentMethods) {
              console.log('🔄 [ApiDataFix XHR] Detected payment methods response in data structure');
              transformPaymentMethodsData(data);
            } else if (isPaymentMethodsQuery) {
              console.log('🔄 [ApiDataFix XHR] Processing payment methods query (from query string)');
              transformPaymentMethodsData(data);
            }
            
            const sanitizedData = sanitizeData(data);
            
            logApiResponse('xhr', xhr._url, data, sanitizedData);
            
            // Override the response with sanitized data
            Object.defineProperty(xhr, 'responseText', {
              get: function() {
                return JSON.stringify(sanitizedData);
              },
              configurable: true
            });
            
            console.log('Intercepted and sanitized XHR response for QuickBooks data');
          } catch (e) {
            logApiResponse('xhr', xhr._url, xhr.responseText, xhr.responseText, e);
            console.warn('Error sanitizing XHR response', e);
          }
        }
      }
      
      if (originalOnReadyStateChange) {
        originalOnReadyStateChange.apply(this, arguments);
      }
    };
    
    return originalXhrSend.apply(this, arguments);
  };
  
  console.log('API Data Fix applied - fetch and XHR calls are now protected with enhanced logging');
}



// WEBPACK FOOTER //
// ./src/utils/patches/apiFix.js